import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { joinKeywords } from '../seo/keywords';
import { absoluteAsset, absoluteUrl, DEFAULT_OG_IMAGE } from '../seo/site';

type SeoProps = {
  title: string;
  description?: string;
  keywords?: string[];
  image?: string;
  canonicalPath?: string;
  jsonLd?: object | object[];
  noIndex?: boolean;
};

function upsertMetaTag(attrName: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attrName}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, data: object | undefined) {
  const existing = document.getElementById(id);
  if (!data) {
    existing?.remove();
    return;
  }
  let el = existing as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export const Seo: React.FC<SeoProps> = ({
  title,
  description,
  keywords,
  image,
  canonicalPath,
  jsonLd,
  noIndex = false,
}) => {
  const { pathname } = useLocation();
  const canonical = absoluteUrl(canonicalPath ?? pathname);
  const ogImage = absoluteAsset(image ?? DEFAULT_OG_IMAGE);
  const jsonLdSerialized = jsonLd
    ? JSON.stringify(Array.isArray(jsonLd) ? { '@context': 'https://schema.org', '@graph': jsonLd } : jsonLd)
    : '';
  const keywordsContent = joinKeywords(keywords);

  useEffect(() => {
    document.title = title;
    upsertLink('canonical', canonical);
    upsertMetaTag('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertMetaTag('property', 'og:site_name', 'Cimmple');
    upsertMetaTag('property', 'og:locale', 'en_US');
    upsertMetaTag('property', 'og:title', title);
    upsertMetaTag('property', 'og:url', canonical);
    upsertMetaTag('property', 'og:image', ogImage);
    upsertMetaTag('name', 'twitter:card', 'summary_large_image');
    upsertMetaTag('name', 'twitter:title', title);
    upsertMetaTag('name', 'twitter:image', ogImage);

    if (keywordsContent) {
      upsertMetaTag('name', 'keywords', keywordsContent);
    }

    if (description) {
      upsertMetaTag('name', 'description', description);
      upsertMetaTag('property', 'og:description', description);
      upsertMetaTag('name', 'twitter:description', description);
    }

    upsertJsonLd('seo-jsonld', jsonLdSerialized ? JSON.parse(jsonLdSerialized) : undefined);
  }, [title, description, keywordsContent, canonical, ogImage, jsonLdSerialized, noIndex]);

  return null;
};
